import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackHandler, Image, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { assets } from '@assets';
import { alertsTestIDs, livePageTestIDs, Theme } from '@constants';
import { useContextNodeHandle, useContextReference } from '@hooks';
import { navigationService } from '@services';
import { ANFState, anfState, screenReaderEnabledState } from '@store';

import { LiveANFContent } from './LiveANFContent/LiveANFContent';
import { OptInContent } from './OptInContent/OptInContent';
import { tPath } from './constants';
import { styles } from './styles';

import { isAndroid, isWeb } from '../../../helpers/Platform';
import { FOCUS_NOW } from '../../../helpers/Tools';

export const AlertLiveANF = () => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });
  const [screenReaderEnabled] = useRecoilState(screenReaderEnabledState);
  const [isModalDismissed, setIsModalDismissed] = useState(false);

  const setANF = useSetRecoilState<ANFState>(anfState);

  const watchButtonHandle = useContextNodeHandle(alertsTestIDs.liveANF.watchNowButton);
  const cancelButtonHandle = useContextNodeHandle(alertsTestIDs.liveANF.cancelButton);
  const gotItButtonReference = useContextReference(alertsTestIDs.liveANF.gotItButton);
  const gotItButtonHandle = useContextNodeHandle(alertsTestIDs.liveANF.gotItButton);

  const dismissAlert = () => navigationService.goBack();

  const onDismissANFAlert = () => {
    if (isAndroid) {
      FOCUS_NOW(gotItButtonReference);
    } else {
      setTimeout(() => {
        FOCUS_NOW(gotItButtonReference);
      }, 10);
    }
    setIsModalDismissed(true);
  };

  const onBackPress = () => {
    if (isModalDismissed) {
      // Dismiss Alert
      dismissAlert();
    } else {
      // Show Opt-In Modal
      onDismissANFAlert();
    }
    return true;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    useCallback(() => {
      BackHandler.addEventListener('hardwareBackPress', onBackPress);
      return () => BackHandler.removeEventListener('hardwareBackPress', onBackPress);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalDismissed, gotItButtonReference]),
  );

  useEffect(() => {
    if (isWeb) {
      setTimeout(() => {
        FOCUS_NOW(watchButtonHandle);
      }, 10);
    }
  }, [watchButtonHandle]);

  const setANFStreamSelection = () => {
    setANF(anfData => {
      const first = anfData?.data && anfData?.data.length > 0 ? anfData?.data[0] : null;

      if (!first?.url) {
        console.error('Invalid ANF state: trying to go to ANF with no active alerts');
        return anfData;
      }
      const newANFAlerts = anfData?.data?.map(anf => ({ ...anf, selected: anf.url === first.url }));

      return {
        ...anfData,
        data: newANFAlerts,
      };
    });
  };

  return (
    <LinearGradient
      colors={[Theme.gradients.alertTile.start, Theme.gradients.alertTile.end]}
      style={styles.gradientWrapper}
    >
      <View accessibilityRole={'text'} accessible={screenReaderEnabled} style={styles.wrapper}>
        <Image
          source={assets.alertIcon}
          style={[styles.alertIcon, { marginBottom: Theme.px(isModalDismissed ? 40 : 26) }]}
        />
        <View style={isModalDismissed ? styles.hideDisplay : undefined}>
          <LiveANFContent
            t={t}
            cancelHandle={isAndroid ? (isModalDismissed ? gotItButtonHandle : cancelButtonHandle) : null}
            nextFocusDown={cancelButtonHandle}
            nextFocusUp={watchButtonHandle}
            watchHandle={isAndroid ? watchButtonHandle : null}
            onCancelPress={onDismissANFAlert}
            onPlayPress={() => {
              dismissAlert();
              navigationService.navigate(livePageTestIDs.livePage);
              setANFStreamSelection();
            }}
          />
        </View>
        <View style={[isModalDismissed ? null : styles.hideDisplay]}>
          <OptInContent
            t={t}
            gotItHandle={isAndroid ? gotItButtonHandle : null}
            startCountDown={isModalDismissed}
            onDidFinishCountDown={dismissAlert}
            onPress={dismissAlert}
          />
        </View>
      </View>
    </LinearGradient>
  );
};
